/* global VideoDecoder, EncodedVideoChunk */

/**
 * PlaybackPlayer — WebCodecs MP4 playback for the unified viewer.
 *
 * Ported from dragon's html/playback.js (class Playback): a self-contained
 * fragmented-MP4 demuxer + H.264/H.265 WebCodecs decoder with a rAF-scheduled
 * playhead, byte-range streaming load, and keyframe-accurate seek/pause. This
 * player handles fragmented dragon clips and non-fragmented mpengine clips
 * served through pbserver. It has no external dependency. Its MP4 parsing is
 * hand-rolled to match dragon.
 *
 * Adapted for React (matching the live PlayerWS port):
 *   - takes a canvas ELEMENT + an options bag of callbacks, not DOM element ids;
 *     the React component owns the transport bar and drives play/pause/seek and
 *     renders time/status/metadata from the callbacks.
 *   - draws overlays through the shared src/player/overlayRenderer.drawOverlays()
 *     instead of dragon's duplicated inline drawing — one source of truth for
 *     objects + LPR across live, playback, and (later) export.
 *
 * Export-with-overlay (dragon's re-decode→draw→re-encode + Mp4Muxer) is NOT
 * ported here: dview-lite already has a server-side export system (#86bapyave),
 * so client-side overlay-burn export is a separate product decision.
 *
 * Callbacks (all optional): onStatus(msg) · onMetadataUpdate(metadata) ·
 * onTimeUpdate(playheadSec, durationSec) · onDurationChange(sec) ·
 * onStateChange('playing'|'paused') · onReady() · onError(err).
 */
import { drawOverlays } from './overlayRenderer';
import { getOverlayPrefs, subscribeOverlayPrefs } from '../common/overlayPrefs';
import { averageFps } from '../util/mcpbVideoInfo';
import { findSeiInSample, parseB2FromSample, b2ToOverlayMetadata } from './b2Sei';
import { hevcCodecStringFromPtl } from '../util/hevcCodec';
import { readDecoderPref, writeDecoderPref, decoderPrefKey, detectPumping } from '../util/decoderPrefs';
import { debugLog } from '../util/debug';

class PlaybackPlayer {

	constructor(canvasElement, fileUrl, options = {}) {
		this.canvas = canvasElement;
		if (!this.canvas) {
			console.error('[PlaybackPlayer] Canvas element not provided');
			return;
		}
		if (!this.canvas.id) {
			this.canvas.id = `playback-${Math.random().toString(36).substr(2, 9)}`;
		}
		this.ctx = this.canvas.getContext("2d");

		this.fileUrl = fileUrl;
		// Optional profile fallback (cloud multi-cam pro2 → pro1): when the primary URL 404s in load()
		// — engine_backend's fast 404 for a clip with no record_pro2 twin — retry ONCE from fallbackUrl.
		// `activeProfile` tracks which profile is actually decoding (surfaced in the Camera-Info overlay)
		// and flips to fallbackProfile on the swap. Generic: the player carries opaque URLs/numbers and
		// never knows the pro1/pro2 naming (the cloud adapter builds both URLs).
		this.fallbackUrl = options.fallbackUrl || null;
		this.activeProfile = options.profile != null ? options.profile : null;
		this._fallbackProfile = options.fallbackProfile != null ? options.fallbackProfile : null;
		this.mq = options.messageQueue || null;
		this.objIds = [];

		// Overlay visibility toggles. Seed from the global overlay prefs (Setup > Display
		// & Theme / in-view control), letting an explicit option override; then subscribe
		// so a live toggle updates this already-decoding player without a reload.
		const prefs = getOverlayPrefs();
		this.showObjects = typeof options.showObjects === "boolean" ? options.showObjects : prefs.objects;
		this.showLpr = typeof options.showLpr === "boolean" ? options.showLpr : prefs.lpr;
		this._overlayUnsub = subscribeOverlayPrefs(p => this.setOverlayVisibility({ showObjects: p.objects, showLpr: p.lpr }));
		this.autoplay = options.autoplay !== false;

		// Playback rate multiplier (1 = real-time). The rAF playhead advances by elapsed*speed, so the
		// picture keeps pace with the shared MCPlayback clock (which ticks at 1000/speed ms) between its
		// 1Hz-scaled ticks — the WebCodecs analog of the <video> path's playbackRate=speed. Runtime-
		// settable via setSpeed() (Java MCTimelineController.setSpeed → PlaybackParser dstream.setPlaySpeed).
		this.speed = Number(options.speed) > 0 ? Number(options.speed) : 1;

		// React integration callbacks (replace dragon's DOM-element wiring).
		this.onStatus = options.onStatus || null;
		this.onMetadataUpdate = options.onMetadataUpdate || null;
		this.onTimeUpdate = options.onTimeUpdate || null;
		this.onDurationChange = options.onDurationChange || null;
		this.onStateChange = options.onStateChange || null;
		// Fired when the playhead reaches the clip's end (the WebCodecs analog of
		// a <video>'s 'ended' event). The single-cam view uses it to auto-advance
		// to the next clip. Fires once per end; play()/seek() re-arm it.
		this.onEnded = options.onEnded || null;
		this._endedFired = false;
		this.onReady = options.onReady || null;
		// Fired ONCE when the first decoded frame is actually painted (not when the decoder is merely
		// configured — that is onReady). The multi-cam buffer-gate keys "this cell is ready" on THIS,
		// the WebCodecs analog of a <video>'s loadeddata, so the shared clock holds until the picture
		// is genuinely up (not just the decoder built) and cams stay in lockstep.
		this.onFirstFrame = options.onFirstFrame || null;
		this._firstFrameRendered = false;
		// Per-paint progress — self-heal analog of useSlotVideo's `timeupdate` (David 2026-08-14). Fired on
		// every presented frame AFTER the first while playing, so a slot hook can clear a stuck buffering
		// latch (a moving picture is never "buffering").
		this.onProgress = options.onProgress || null;
		// A paused first-paint target (rehydrate / paused entry): show a STILL frame while paused instead
		// of a spinner. If the keyframe's bytes aren't streamed in yet when requested, it's retried from
		// the load loop as bytes arrive (see _tryFirstPaint) so the cell never sits on a spinner paused.
		this._pendingFirstPaintSec = null;
		this.onError = options.onError || null;

		// Track info populated from moov
		this.videoTrack = null;   // {id, codec, description, timescale, width, height}
		this.metaTrack  = null;   // {id, timescale}
		this.mvhdTimescale = 0;
		this.durationSec = 0;

		// Sample index (per track, appended as fragments arrive, in file order)
		this.videoSamples = [];
		this.metaSamples  = [];

		// Body buffer: bytes from bodyOffset onward (covers moof/mdat fragments)
		this.bodyOffset = 0;
		this.bodyLen = 0;
		this.bodyBytes = null;
		this.bodyEnd = 0;          // absolute file offset of last byte received
		this._parseCursor = 0;     // absolute file offset, parser walked past

		this.totalSize = 0;
		this.moovEnd = 0;

		// Decoder + playback state
		this.decoder = null;
		this.metadata = null;
		// Decoder hw/sw preference — learned per codec+resolution (the decode workload) and stored in
		// localStorage (per-browser). Hardware pumps some low-fps surveillance H.264 on some GPUs; we
		// detect that from the render cadence and fall back to software. Read in _onReady (needs the
		// parsed codec/dims); _renderFrame samples the cadence; _checkForPumping decides + persists.
		this.useHardwareAccel = true;
		this._renderDeltas = [];
		this._pumpCheckDone = false;
		this.playing = false;
		this.playheadSec = 0;
		this.playheadAtStart = 0;
		this.startWallTime = 0;
		this.rafHandle = null;
		this.nextVideoIdx = 0;
		this.nextMetaIdx  = 0;
		this.targetSeekTsMicros = null;
		this.bufferedMaxSec = 0;
		this.loadComplete = false;
		// A fragmented clip's empty moov carries no duration. Until its real length is read from the
		// trailing mfra index (ADR-172, ported from dragon), durationSec stays 0 instead of tracking the
		// downloaded length: a length that grows with the download made _tick "end" the clip at the
		// download edge, pausing the cell while the shared clock kept running. Without an index the
		// length is estimated from the share of bytes downloaded.
		this._durationSettled = true;
		this._durationEstimated = false;
		// True only when the body stream reached EOF cleanly (not a failed or aborted read).
		this._bodyEOF = false;
		// Body-stream generation + abort: seek() supersedes an in-flight body stream to re-anchor the
		// mdat fetch at the seek target's byte offset (byte-range seek). The generation token makes a
		// superseded stream's callbacks no-ops; its own AbortController cancels the in-flight fetch.
		this._bodyGen = 0;
		this._bodyAbort = null;
		// Windowed body streaming (non-fragmented clips): one window in flight at a time, retried with a
		// short backoff when a window fails (see _maybeRefillBody / _bodyStreamFailed).
		this._bodyInFlight = false;
		this._refillFailures = 0;
		this._refillRetryAt = 0;
		// A paused seek must flush the decoder to emit its still frame, but only AFTER the keyframe is
		// fed (bytes may arrive post-seek via the re-anchor). _feedVideo does the deferred flush.
		this._seekFlushPending = false;

		// Fragmentation: cloud-dragon clips are fragmented (moof/mdat) and carry a
		// JSON metadata track; local mpengine clips (pbserver) are non-fragmented
		// (moov + stbl) and carry b2 SEI in the video NALs. Set in load().
		this.isFragmented = true;
		// b2 SEI metadata, keyed by composition-time micros (matches
		// EncodedVideoChunk.timestamp / VideoFrame.timestamp) so B-frame display
		// order resolves to the right camera name / overlays. Parsed lazily per
		// rendered frame; null is cached to avoid re-parsing SEI-less samples.
		this.seiByCtsMicros = new Map();
		this._sampleIdxByCtsMicros = new Map();
		this._lastSeiSeen = null;
		// Draw the SEI camera-name + timestamp HUD on the canvas (local clips only).
		this.showHud = options.showHud !== false;

		// Cover the complete async load lifecycle, including the initial probe.
		// React can unmount the player before moov parsing finishes.
		this.abortController = new AbortController();
		this._stopped = false;
		this.frameQueue = [];
		this._seekPending = false;
		// Frames with timestamp < this are discarded in handleFrame — protects against
		// stale outputs that slip through after decoder.reset().
		this._minAcceptTsMicros = 0;
		// Set whenever the decoder is (re)configured or flushed — the next decode()
		// must be a key chunk, or Chrome throws "A key frame is required after
		// configure() or flush()." Cleared once a keyframe is fed.
		this._needsKeyframe = false;
		// Throttle for onTimeUpdate — the rAF loop ticks ~60fps; React must not.
		this._lastEmitSec = null;

		// Rolling fps estimate for the "Camera Info overlay" (getStreamInfo) — the WebCodecs analog of
		// useVideoInfo's requestVideoFrameCallback rate: one sample per PRESENTED frame (in _renderFrame),
		// so it reflects the true painted cadence, not the ~60fps rAF tick.
		this._fpsSamples = [];
		this._lastRenderWall = 0;

		this.setOverlay("Loading…");
		this.load().catch(e => {
			if (this._stopped || e.name === 'AbortError') return;
			console.error(`[${this.canvas.id}] Load failed:`, e);
			this.setOverlay("Error: " + (e.message || e));
			if (this.onError) this.onError(e);
		});
	}


	// ---- Small helpers (ported from playerWS.js) ----

	setOverlay(msg) { if (this.onStatus) this.onStatus(msg); }

	isObjNew(id) {
		if (this.objIds.includes(id)) return false;
		if (this.objIds.length > 100) this.objIds.shift();
		this.objIds.push(id);
		return true;
	}

	fadeImage() {
		this.ctx.fillStyle = "blue";
		this.ctx.globalAlpha = 0.5;
		this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
		this.ctx.globalAlpha = 1.0;
	}

	async cropObjectJPEG(px, py, pw, ph) {
		const tempCanvas = document.createElement("canvas");
		tempCanvas.width = pw;
		tempCanvas.height = ph;
		const tctx = tempCanvas.getContext("2d");
		tctx.drawImage(this.canvas, px, py, pw, ph, 0, 0, pw, ph);
		return new Promise((resolve) => {
			tempCanvas.toBlob((blob) => {
				if (!blob) return resolve(null);
				const reader = new FileReader();
				reader.onloadend = () => resolve(reader.result);
				reader.readAsDataURL(blob);
			}, "image/jpeg", 0.85);
		});
	}

	// Publish newly-seen object detections to the message queue — a PLAYER
	// side-effect, on the CLEAN frame before overlays draw (identical to the live
	// PlayerWS path, so thumbnails never bake in overlay boxes).
	publishObjectEvents() {
		if (!this.mq || !this.metadata?.objects) return;
		for (const obj of this.metadata.objects) {
			if (!obj || !Array.isArray(obj.bbox) || obj.bbox.length !== 4) continue;
			if (!this.isObjNew(obj.id)) continue;
			const [x, y, w, h] = obj.bbox;
			const px = x * this.canvas.width;
			const py = y * this.canvas.height;
			const pw = w * this.canvas.width;
			const ph = h * this.canvas.height;
			(async () => {
				const image = await this.cropObjectJPEG(px, py, pw, ph);
				this.mq.publish("objectEvent", { id: obj.id, label: obj.label, image });
			})();
		}
	}

	// Toggle overlay layers at runtime from the shared display preferences.
	setOverlayVisibility({ showObjects, showLpr } = {}) {
		if (typeof showObjects === "boolean") this.showObjects = showObjects;
		if (typeof showLpr === "boolean") this.showLpr = showLpr;
	}

	// Live stream stats for the "Camera Info overlay" — the WebCodecs analog of reading a <video>'s
	// videoWidth/videoHeight + rVFC fps (hooks/useVideoInfo). Resolution comes from the decoded frame
	// (canvas), falling back to the moov track dims before the first paint; fps is the rolling
	// presented-frame estimate, null when playback is paused/stalled (no recent paint → "— fps").
	getStreamInfo() {
		const STALE_MS = 1500;
		const nowWall = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
		const fresh = this.playing && this._lastRenderWall && (nowWall - this._lastRenderWall) < STALE_MS;
		return {
			width: this.canvas.width || (this.videoTrack && this.videoTrack.width) || 0,
			height: this.canvas.height || (this.videoTrack && this.videoTrack.height) || 0,
			fps: fresh ? averageFps(this._fpsSamples) : null,
			// The profile ACTUALLY decoding (reflects a pro2→pro1 fallback if it fired). null when the
			// player wasn't told a profile (local/pbserver) — the overlay then uses its own hint.
			profile: this.activeProfile,
		};
	}


	// ---- Byte readers ----

	_readU32(buf, o) {
		return ((buf[o] << 24) | (buf[o+1] << 16) | (buf[o+2] << 8) | buf[o+3]) >>> 0;
	}
	_readU16(buf, o) { return ((buf[o] << 8) | buf[o+1]) >>> 0; }
	_readU64(buf, o) { return this._readU32(buf, o) * 4294967296 + this._readU32(buf, o+4); }
	_readType(buf, o) { return String.fromCharCode(buf[o], buf[o+1], buf[o+2], buf[o+3]); }

	// Walk direct children of a container box. fn(type, start, end, hdrSize) per child.
	_walkBoxes(buf, start, end, fn) {
		let p = start;
		while (p + 8 <= end) {
			const size = this._readU32(buf, p);
			const type = this._readType(buf, p + 4);
			let boxEnd, hdr = 8;
			if (size === 1) {
				if (p + 16 > end) break;
				boxEnd = p + this._readU64(buf, p + 8);
				hdr = 16;
			} else if (size === 0) {
				boxEnd = end;
			} else {
				boxEnd = p + size;
			}
			if (boxEnd > end || boxEnd <= p) break;
			fn(type, p, boxEnd, hdr);
			p = boxEnd;
		}
	}


	// ---- Load pipeline ----

	async load() {
		// Probe head (ftyp + moov); 64KB is plenty for empty_moov fragmented MP4.
		const probeLen = 65536;
		// NVR (pbserver) clips: read the file's tail in parallel with the head. Upgraded NVRs record
		// tail-moov clips (ADR-091), so the index then costs one round trip instead of two. Aborted as soon
		// as the head shows its own moov (faststart or fragmented), so those layouts lose a partial read.
		const probeUrl = this.fileUrl;
		const headReq = fetch(this.fileUrl, {
			headers: { Range: `bytes=0-${probeLen - 1}` },
			signal: this.abortController.signal,
		});
		const tailAc = /\/camstream\//.test(probeUrl) ? new AbortController() : null;
		if (tailAc) this.abortController.signal.addEventListener('abort', () => tailAc.abort());
		const tailProbe = tailAc ? this._probeTail(probeUrl, tailAc.signal) : null;
		let resp = await headReq;
		// Profile fallback: the primary clip 404s when the device has no such file — for cloud multi-cam
		// this is engine_backend's fast 404 for a pro2 twin a clip was recorded without (record_pro2 off
		// for that clip). Swap to the main-profile URL ONCE and re-probe: fast + reliable (no device
		// round-trip, no 504 wait). fileUrl now points at the fallback, so every downstream fetch
		// (_streamBodyFrom / seek re-anchor) inherits it.
		if (resp.status === 404 && this.fallbackUrl && this.fileUrl !== this.fallbackUrl) {
			this.fileUrl = this.fallbackUrl;
			if (tailAc) { try { tailAc.abort(); } catch (_) {} } // that tail belongs to the missing file
			if (this._fallbackProfile != null) this.activeProfile = this._fallbackProfile;
			resp = await fetch(this.fileUrl, {
				headers: { Range: `bytes=0-${probeLen - 1}` },
				signal: this.abortController.signal,
			});
		}
		// engine_backend 416s a range whose end is past EOF (strict), so a clip smaller than
		// the probe window fails the ranged probe — fall back to a full GET so tiny clips load.
		if (resp.status === 416) resp = await fetch(this.fileUrl, { signal: this.abortController.signal });
		if (!resp.ok && resp.status !== 206 && resp.status !== 200) throw new Error(`HTTP ${resp.status}`);
		const cr = resp.headers.get('Content-Range');
		if (cr) {
			const m = cr.match(/\/(\d+)$/);
			if (m) this.totalSize = parseInt(m[1], 10);
		}
		const headStatus = resp.status;
		let head = new Uint8Array(await resp.arrayBuffer());
		if (this._stopped) return;

		// Ensure moov is complete.
		let info = this._findMoov(head);
		// Tail moov (mpengine's `end` layout for upgraded NVRs, ADR-091: ftyp, free, mdat, moov). The
		// head walk stepped over the mdat by its declared size, so the moov starts at info.end - 8. Read
		// that tail (5-25 KB) instead of downloading the whole mdat in front of it: the forward read below
		// pulled 17-20 MB per profile-2 clip before one frame could decode, a 30-60 s blank tile over a
		// store uplink (86bc8571y).
		let tailMoov = null;
		if (!info.complete && info.start < 0) {
			const moovStart = info.end - 8;
			// Bounded: a server that answers the head but stalls the suffix read must not hold the load; the
			// exact tail range below takes over.
			let probeTimer = null;
			const probeTimeout = new Promise((res) => { probeTimer = setTimeout(() => res(null), PlaybackPlayer.TAIL_PROBE_TIMEOUT_MS); });
			const probed = (tailProbe && this.fileUrl === probeUrl) ? await Promise.race([tailProbe, probeTimeout]) : null;
			clearTimeout(probeTimer);
			if (!probed && tailAc) { try { tailAc.abort(); } catch (_) {} }
			tailMoov = this._moovFromTail(probed, moovStart) || await this._fetchTailMoov(moovStart, head.byteLength);
		} else if (tailAc) {
			try { tailAc.abort(); } catch (_) {}
		}
		if (this._stopped) return;
		if (tailMoov) {
			this._parseMoov(tailMoov.buf, tailMoov.start, tailMoov.end);
			this.moovEnd = tailMoov.absEnd;
		} else {
			while (!info.complete) {
				if (info.end <= head.byteLength) throw new Error("moov not found");
				const upto = Math.min(this.totalSize || info.end, info.end) - 1;
				const more = await fetch(this.fileUrl, {
					headers: { Range: `bytes=${head.byteLength}-${upto}` },
					signal: this.abortController.signal,
				});
				if (!more.ok && more.status !== 206 && more.status !== 200) throw new Error(`HTTP ${more.status}`);
				const moreBytes = new Uint8Array(await more.arrayBuffer());
				if (this._stopped) return;
				if (moreBytes.byteLength === 0) throw new Error("unable to complete moov fetch");
				if (more.status === 200) {
					// The server ignored the nonzero Range and returned the complete file.
					head = moreBytes;
				} else {
					const combined = new Uint8Array(head.byteLength + moreBytes.byteLength);
					combined.set(head, 0);
					combined.set(moreBytes, head.byteLength);
					head = combined;
				}
				info = this._findMoov(head);
			}

			this._parseMoov(head, info.start, info.end);
			this.moovEnd = info.end;
		}

		// Non-fragmented (local mpengine, moov + stbl) vs fragmented (cloud dragon,
		// empty_moov + moof/mdat). If the video track carries a full sample table,
		// build it now; otherwise fall back to the fragment parser.
		const nonFragCount = this._buildNonFragSamples();
		// A tail moov always carries the full sample table; without one there is nothing to play.
		if (tailMoov && nonFragCount === 0) throw new Error("tail moov has no sample table");
		if (nonFragCount > 0) {
			// Sample byteOffsets are absolute file offsets, so the body buffer is
			// addressed from offset 0. Seed it with the bytes already fetched;
			// _streamBodyFrom appends later windows at absolute offsets.
			this.isFragmented = false;
			this.bodyOffset = 0;
			this.bodyBytes = head.slice();
			this.bodyLen = head.byteLength;
			this.bodyEnd = head.byteLength;
			this._parseCursor = head.byteLength;
			this._updateBuffered();
		} else {
			this.isFragmented = true;
			this.bodyOffset = this.moovEnd;
			this._parseCursor = this.bodyOffset;

			// Preallocate body buffer (grows dynamically if needed)
			const bodyEstimate = Math.max(0, (this.totalSize || 0) - this.moovEnd);
			this.bodyBytes = new Uint8Array(bodyEstimate || 4 * 1024 * 1024);

			this._durationSettled = false;
			// A head without Content-Range means the server is not serving ranges; don't send it a
			// suffix read (a range-less backend could pull the whole clip to answer it).
			// A 206 whose Content-Range is unreadable (cross-origin without Expose-Headers) stays unsettled
			// until a clean EOF, so the length never tracks the download edge.
			if (this.totalSize) this._probeDurationAsync();
			else if (headStatus !== 206) { this._durationSettled = true; this._durationEstimated = true; }

			// If head already contained fragment bytes past moov, absorb them.
			if (head.byteLength > this.moovEnd) {
				const tail = head.subarray(this.moovEnd);
				this._appendBody(tail, this.moovEnd);
				this._parseBody();
			}
		}

		if (this._stopped) return;
		this._onReady();

		// Stream the mdat body forward from where the head left off — but ONLY if a seek
		// (requestFirstPaint during _onReady) hasn't already anchored a stream at the target. Without
		// this guard, load() and seek() both fire _streamBodyFrom for the same target range, issuing a
		// duplicate fetch that competes for bandwidth (halving effective throughput over a slow WAN).
		if (!this._bodyGen) await this._streamBodyFrom(this.bodyOffset + this.bodyLen);
	}

	// Stream the mdat body from absolute offset `startByte` to EOF (206/streamed), appending into the
	// body buffer as chunks arrive so samples become decodable progressively. Re-callable: each call
	// bumps a generation token and aborts the prior stream's fetch, so seek() can re-anchor the body
	// at the target keyframe's offset (byte-range seek) instead of streaming the whole mdat from the
	// front. POST-READY, so a tail failure is non-fatal — play what's buffered rather than blank the
	// cell. engine_backend serves each clip 200/206 but 416s a range at/after EOF (verified
	// 2026-07-17); a fetch that lands past EOF must read as "done", not "error".
	async _streamBodyFrom(startByte) {
		const gen = this._bodyGen = (this._bodyGen || 0) + 1;
		this._bodyEOF = false; // per stream: a re-anchored stream must reach its own EOF
		try { if (this._bodyAbort) this._bodyAbort.abort(); } catch (_) {}
		const ac = this._bodyAbort = new AbortController();

		const streamEnd = this.totalSize;
		if (streamEnd && startByte >= streamEnd) { this.loadComplete = true; this._bodyEOF = true; this._bodyInFlight = false; this._updateBuffered(); return; }

		// Non-fragmented clips know every sample's byte range up front, so they stream in bounded
		// WINDOWS instead of to EOF: a few seconds while paused (a first paint, or a warm next-clip
		// player), more while playing, topped up from _tick by _maybeRefillBody. Streaming every clip to
		// EOF put eight whole-clip reads on one NVR for a 4-up grid (four tiles plus four warm next
		// clips): the store uplink saturated, the browser's six-connection limit queued the seek's own
		// request behind them, and tiles sat blank for tens of seconds (86bc8571y).
		const windowEnd = this._bodyWindowEnd(startByte);
		this._bodyInFlight = true;
		let bodyResp;
		try {
			const end = windowEnd != null ? windowEnd : (streamEnd ? streamEnd - 1 : null);
			const tailRange = end != null ? `bytes=${startByte}-${end}` : `bytes=${startByte}-`;
			bodyResp = await fetch(this.fileUrl, { headers: { Range: tailRange }, signal: ac.signal });
		} catch (e) {
			if (gen === this._bodyGen) this._bodyInFlight = false;
			if (e.name === 'AbortError') return;
			console.warn(`[${this.canvas.id}] body fetch failed (non-fatal):`, e.message || e);
			if (gen === this._bodyGen) this._bodyStreamFailed(windowEnd != null);
			return;
		}
		if (gen !== this._bodyGen || this._stopped) return;
		// 416 = the range starts at/after EOF — nothing more to stream; what we have plays.
		if (bodyResp.status === 416) { this.loadComplete = true; this._bodyEOF = true; this._bodyInFlight = false; this._parseBody(); this._updateBuffered(); return; }
		if (!bodyResp.ok && bodyResp.status !== 206 && bodyResp.status !== 200) {
			console.warn(`[${this.canvas.id}] body HTTP ${bodyResp.status} (non-fatal — playing buffered)`);
			this._bodyInFlight = false;
			this._bodyStreamFailed(windowEnd != null);
			return;
		}
		const reader = bodyResp.body.getReader();
		// A server can ignore Range and return the complete representation with 200. Then the response
		// starts at file offset zero, and the Math.max below skips the prefix before the body anchor.
		let fileOffset = bodyResp.status === 200 ? 0 : startByte;
		let eof = false;
		try {
			while (true) {
				const { value, done } = await reader.read();
				if (done) { eof = true; break; }
				// A newer stream (a seek re-anchor) has superseded this one — stop appending stale bytes.
				if (gen !== this._bodyGen || this._stopped) { try { reader.cancel(); } catch (_) {} return; }
				const chunkStart = fileOffset;
				fileOffset += value.byteLength;
				// Skip any full-file (status 200) prefix before the current body anchor.
				const appendAt = Math.max(chunkStart, this.bodyOffset);
				const skip = appendAt - chunkStart;
				if (skip < value.byteLength) this._appendBody(value.subarray(skip), appendAt);
				this._parseBody();
				this._updateBuffered();
				this._tryFirstPaint();   // paint the seek / first-paint target as soon as its bytes arrive
			}
		} catch (e) {
			if (e.name !== 'AbortError') console.warn(`[${this.canvas.id}] stream ended (non-fatal):`, e.message || e);
		}
		if (gen !== this._bodyGen) return;
		this._bodyInFlight = false;
		if (windowEnd != null) {
			// A window ending is not the clip ending: only the last byte of the file completes the load.
			const atEOF = this.bodyEnd >= streamEnd;
			this.loadComplete = atEOF;
			if (eof && atEOF) this._bodyEOF = true;
			// A window that closed without adding a byte is a failure too, or the refill would re-request
			// the same range on every frame.
			// A window that delivered bytes resets the count even if its read then broke, so a link that
			// drops a connection every few MB keeps playing (the broken window still backs off once).
			const progressed = this.bodyEnd > startByte;
			if (progressed) { this._refillFailures = 0; this._refillRetryAt = 0; }
			if (!(eof && progressed) && !atEOF) this._bodyStreamFailed(true);
		} else {
			this.loadComplete = true;
			if (eof) this._bodyEOF = true;
		}
		this._parseBody();
		this._updateBuffered();
		this._tryFirstPaint();
	}

	// End byte (inclusive) of the body window that starts at `startByte`, or null to stream to EOF.
	// Only non-fragmented clips window (their sample table gives every byte range up front); the
	// window covers BODY_WINDOW_PAUSED_SEC of samples while paused and BODY_WINDOW_PLAY_SEC while
	// playing, and ends just before the first sample past it so the next window starts on a sample.
	_bodyWindowEnd(startByte) {
		if (this.isFragmented || !this.totalSize || !this.videoTrack || !this.videoSamples.length) return null;
		const samples = this.videoSamples;
		let lo = 0;
		let hi = samples.length;
		while (lo < hi) { // first sample at/after startByte (samples are in file order)
			const mid = (lo + hi) >> 1;
			if (samples[mid].byteOffset < startByte) lo = mid + 1; else hi = mid;
		}
		if (lo >= samples.length) return null;
		// Media seconds: a fast-forward plays them faster, so the playing window scales with the speed.
		const sec = this.playing ? PlaybackPlayer.BODY_WINDOW_PLAY_SEC * Math.max(1, this.speed || 1) : PlaybackPlayer.BODY_WINDOW_PAUSED_SEC;
		const limit = samples[lo].dts + sec * this.videoTrack.timescale;
		let j = lo;
		while (j < samples.length && samples[j].dts <= limit) j++;
		if (j >= samples.length) return null; // the window reaches the last sample: finish the clip
		return Math.min(this.totalSize - 1, samples[j].byteOffset - 1);
	}

	// Top up a windowed body stream while playing, once the playhead is within
	// BODY_REFILL_LOW_WATER_SEC of the end of what is buffered. Paused players refill only to retry a
	// failed window for a pending seek, so a warm next-clip player holds only its first window until it
	// is promoted and plays.
	_maybeRefillBody() {
		if (this.isFragmented || this.loadComplete || this._bodyInFlight || !this.totalSize) return;
		if (!this.videoTrack || !this.videoSamples.length) return;
		if (this._refillRetryAt && performance.now() < this._refillRetryAt) return;
		if (this.bodyEnd >= this.totalSize) { this.loadComplete = true; return; }
		const samples = this.videoSamples;
		const lowWater = PlaybackPlayer.BODY_REFILL_LOW_WATER_SEC * Math.max(1, this.speed || 1);
		const horizon = (this.playheadSec + lowWater) * this.videoTrack.timescale;
		let j = Math.min(this.nextVideoIdx || 0, samples.length - 1);
		while (j + 1 < samples.length && samples[j + 1].dts <= horizon) j++;
		const s = samples[j];
		if (s.byteOffset + s.size > this.bodyEnd) this._streamBodyFrom(this.bodyEnd);
	}

	// A body stream failed. Windowed streams back off and retry from _maybeRefillBody (playing, or a
	// pending paused seek), then give up after BODY_REFILL_MAX_FAILURES: what is buffered still plays,
	// and _reportBodyGaveUp raises onError when the missing bytes are reached.
	_bodyStreamFailed(windowed) {
		if (windowed) {
			this._refillFailures = (this._refillFailures || 0) + 1;
			if (this._refillFailures < PlaybackPlayer.BODY_REFILL_MAX_FAILURES) {
				this._refillRetryAt = performance.now() + 1000 * this._refillFailures;
				return;
			}
		}
		this.loadComplete = true;
		this._updateBuffered();
		if (windowed) {
			this._bodyGaveUp = true;
			this._reportBodyGaveUp();
		}
	}

	// After a windowed stream gave up: once the next sample to decode is not buffered (a pending seek
	// waiting on it, or playback reaching the buffered end), end any pending seek and report the failure
	// once, so the tile falls back to the native player instead of a blank or frozen canvas.
	_reportBodyGaveUp() {
		if (!this._bodyGaveUp || this._bodyGaveUpReported) return;
		if (!this._seekPending && !this.playing) return;
		const next = this.videoSamples[this.nextVideoIdx];
		if (!next || this._sliceSample(next)) return; // the clip's end, or bytes still to play
		this._bodyGaveUpReported = true;
		this._seekPending = false;
		if (this.onError) this.onError(new Error('clip body could not be downloaded'));
	}

	// Suffix read of the last TAIL_PROBE_BYTES, started alongside the head probe. Resolves to
	// { start, total, buf } on a 206, or null (never throws: the head probe owns error reporting).
	async _probeTail(url, signal) {
		try {
			const r = await fetch(url, { headers: { Range: `bytes=-${PlaybackPlayer.TAIL_PROBE_BYTES}` }, signal });
			const m = ((r.headers && r.headers.get('Content-Range')) || '').match(/bytes (\d+)-\d+\/(\d+)$/);
			// A server that answers a suffix range with the whole file (206 bytes 0-(N-1)/N) must not be
			// buffered: that is the full-clip download this probe exists to avoid.
			if (r.status !== 206 || !m || parseInt(m[2], 10) - parseInt(m[1], 10) > PlaybackPlayer.TAIL_PROBE_BYTES) {
				try { if (r.body && r.body.cancel) r.body.cancel(); } catch (_) {}
				return null;
			}
			return { start: parseInt(m[1], 10), total: parseInt(m[2], 10), buf: new Uint8Array(await r.arrayBuffer()) };
		} catch (e) {
			return null;
		}
	}

	// The moov out of a tail probe when the probe covers it; null when it does not (a moov bigger than
	// the probe, or a different file size), and the caller reads the exact tail range instead.
	_moovFromTail(probed, moovStart) {
		if (!probed || !this.totalSize || probed.total !== this.totalSize || moovStart < probed.start) return null;
		const buf = probed.buf.subarray(moovStart - probed.start);
		const t = this._findMoov(buf);
		if (!t.complete || t.start < 0) return null;
		return { buf, start: t.start, end: t.end, absEnd: moovStart + t.end };
	}

	// Read a tail moov with one range: `bytes=<tailStart>-<EOF>`. Returns null, and the caller falls
	// back to the forward read, when the size is unknown, the tail is implausibly large for an index,
	// or the server does not answer the range with a 206, or the read fails (a stop still aborts load).
	async _fetchTailMoov(tailStart, headLen) {
		const total = this.totalSize;
		if (!total || tailStart <= headLen || tailStart >= total) return null;
		if (total - tailStart > PlaybackPlayer.TAIL_MOOV_MAX_BYTES) return null;
		let buf;
		try {
			const r = await fetch(this.fileUrl, {
				headers: { Range: `bytes=${tailStart}-${total - 1}` },
				signal: this.abortController.signal,
			});
			if (r.status !== 206) {
				try { if (r.body && r.body.cancel) r.body.cancel(); } catch (_) {}
				return null;
			}
			buf = new Uint8Array(await r.arrayBuffer());
		} catch (e) {
			if (e.name === 'AbortError' || this._stopped) throw e;
			return null;
		}
		const t = this._findMoov(buf);
		if (!t.complete || t.start < 0) return null;
		return { buf, start: t.start, end: t.end, absEnd: tailStart + t.end };
	}

	_findMoov(buf) {
		let p = 0;
		while (p + 8 <= buf.byteLength) {
			const size = this._readU32(buf, p);
			const type = this._readType(buf, p + 4);
			let boxEnd;
			if (size === 1) {
				if (p + 16 > buf.byteLength) return { start: p, end: p + 16, complete: false };
				boxEnd = p + this._readU64(buf, p + 8);
			} else if (size === 0) {
				boxEnd = buf.byteLength;
			} else {
				boxEnd = p + size;
			}
			if (type === 'moov')
				return { start: p, end: boxEnd, complete: boxEnd <= buf.byteLength };
			if (boxEnd <= p) return { start: -1, end: -1, complete: false };
			p = boxEnd;
		}
		return { start: -1, end: p + 8, complete: false };
	}


	// ---- moov parsing ----

	_parseMoov(buf, start, end) {
		this._walkBoxes(buf, start + 8, end, (t, s, e, h) => {
			if (t === 'mvhd') {
				const version = buf[s + h];
				const o = s + h + 4;
				let timescale, duration;
				if (version === 1) {
					timescale = this._readU32(buf, o + 16);
					duration  = this._readU64(buf, o + 20);
				} else {
					timescale = this._readU32(buf, o + 8);
					duration  = this._readU32(buf, o + 12);
				}
				this.mvhdTimescale = timescale;
				this.durationSec = timescale ? (duration / timescale) : 0;
			} else if (t === 'trak') {
				this._parseTrak(buf, s + h, e);
			}
		});
	}

	_parseTrak(buf, start, end) {
		const track = {
			id: 0, timescale: 0, handler: '', handlerName: '',
			codec: null, description: null, width: 0, height: 0,
		};
		this._walkBoxes(buf, start, end, (t, s, e, h) => {
			if (t === 'tkhd') {
				const version = buf[s + h];
				const o = s + h + 4;
				track.id = (version === 1) ? this._readU32(buf, o + 16) : this._readU32(buf, o + 8);
			} else if (t === 'mdia') {
				this._walkBoxes(buf, s + h, e, (t2, s2, e2, h2) => {
					if (t2 === 'mdhd') {
						const version = buf[s2 + h2];
						const o = s2 + h2 + 4;
						track.timescale = (version === 1)
							? this._readU32(buf, o + 16)
							: this._readU32(buf, o + 8);
					} else if (t2 === 'hdlr') {
						const o = s2 + h2 + 4;
						track.handler = this._readType(buf, o + 4);
						const nameStart = o + 20;
						let nameEnd = nameStart;
						while (nameEnd < e2 && buf[nameEnd] !== 0) nameEnd++;
						track.handlerName = new TextDecoder().decode(buf.subarray(nameStart, nameEnd));
					} else if (t2 === 'minf') {
						this._walkBoxes(buf, s2 + h2, e2, (t3, s3, e3, h3) => {
							if (t3 !== 'stbl') return;
							this._walkBoxes(buf, s3 + h3, e3, (t4, s4, e4, h4) => {
								// o = start of the fullbox payload (version/flags); entry_count at o+4.
								const o = s4 + h4;
								if (t4 === 'stsd') { this._parseStsd(buf, s4 + h4, e4, track); return; }
								if (!track.stbl) track.stbl = {};
								if (t4 === 'stts') track.stbl.stts = this._parseStts(buf, o);
								else if (t4 === 'ctts') track.stbl.ctts = this._parseCtts(buf, o);
								else if (t4 === 'stsc') track.stbl.stsc = this._parseStsc(buf, o);
								else if (t4 === 'stsz') track.stbl.stsz = this._parseStsz(buf, o);
								else if (t4 === 'stco') track.stbl.chunkOffsets = this._parseStco(buf, o);
								else if (t4 === 'co64') track.stbl.chunkOffsets = this._parseCo64(buf, o);
								else if (t4 === 'stss') track.stbl.stss = this._parseStss(buf, o);
							});
						});
					}
				});
			}
		});

		if (track.handler === 'vide') {
			this.videoTrack = track;
		} else if (track.handler !== 'soun' && track.handler !== 'hint') {
			this.metaTrack = track;
		}
	}

	_parseStsd(buf, start, end, track) {
		// full box: 4 bytes version/flags, 4 bytes entry_count
		let p = start + 8;
		if (p + 8 > end) return;
		const entrySize = this._readU32(buf, p);
		const entryType = this._readType(buf, p + 4);
		const entryEnd  = p + entrySize;
		if (entryEnd > end) return;

		// Visual sample entry: 8 (box hdr) + 6 reserved + 2 data_ref + 16 + 2+2 w/h + 4+4+4 + 2 frame_count + 32 comp_name + 2 depth + 2 pre_defined = 86 bytes header
		if (entryType === 'avc1' || entryType === 'avc3' ||
		    entryType === 'hev1' || entryType === 'hvc1') {
			track.codecKind = (entryType === 'avc1' || entryType === 'avc3') ? 'h264' : 'hevc';
			track.width  = this._readU16(buf, p + 32);
			track.height = this._readU16(buf, p + 34);
			const childrenStart = p + 86;
			this._walkBoxes(buf, childrenStart, entryEnd, (t, s, e, h) => {
				if (t === 'avcC') {
					const desc = buf.subarray(s + h, e).slice();
					track.description = desc;
					track.codec = 'avc1.' + [desc[1], desc[2], desc[3]]
						.map(b => b.toString(16).padStart(2, '0').toUpperCase()).join('');
					// avcC byte 4: bits 1..0 = lengthSizeMinusOne (NAL length prefix size).
					track.nalLengthSize = (desc[4] & 0x03) + 1;
				} else if (t === 'hvcC') {
					const desc = buf.subarray(s + h, e).slice();
					track.description = desc;
					track.codec = this._makeHevcCodecString(entryType, desc);
					// hvcC byte 21: bits 1..0 = lengthSizeMinusOne.
					track.nalLengthSize = (desc[21] & 0x03) + 1;
				}
			});
		}
	}

	_makeHevcCodecString(fourcc, desc) {
		// hvcC: version(1), then the 12-byte general profile_tier_level at desc[1..12]
		// (profile_byte, compat[4], constraint[6], level_idc), shared with the live
		// SPS path via hevcCodecStringFromPtl so the two never drift.
		return hevcCodecStringFromPtl(desc.subarray(1, 13), fourcc);
	}

	// ---- Non-fragmented sample-table parsers (stbl) ----
	// Local mpengine clips are non-fragmented: a single moov with a full sample
	// table, no moof. `o` points at each fullbox payload (version/flags), so
	// entry_count is at o+4 and entries follow at o+8.

	_parseStts(buf, o) {
		const count = this._readU32(buf, o + 4);
		const out = [];
		let p = o + 8;
		for (let i = 0; i < count; i++) {
			out.push({ count: this._readU32(buf, p), delta: this._readU32(buf, p + 4) });
			p += 8;
		}
		return out;
	}

	_parseCtts(buf, o) {
		const ver = buf[o];
		const count = this._readU32(buf, o + 4);
		const out = [];
		let p = o + 8;
		for (let i = 0; i < count; i++) {
			let off = this._readU32(buf, p + 4);
			if (ver === 1) off = off | 0; // v1 offsets are signed
			out.push({ count: this._readU32(buf, p), offset: off });
			p += 8;
		}
		return out;
	}

	_parseStsc(buf, o) {
		const count = this._readU32(buf, o + 4);
		const out = [];
		let p = o + 8;
		for (let i = 0; i < count; i++) {
			out.push({
				firstChunk: this._readU32(buf, p),
				samplesPerChunk: this._readU32(buf, p + 4),
				descIdx: this._readU32(buf, p + 8),
			});
			p += 12;
		}
		return out;
	}

	_parseStsz(buf, o) {
		const sampleSize = this._readU32(buf, o + 4);
		const count = this._readU32(buf, o + 8);
		const sizes = [];
		if (sampleSize === 0) {
			let p = o + 12;
			for (let i = 0; i < count; i++) { sizes.push(this._readU32(buf, p)); p += 4; }
		} else {
			for (let i = 0; i < count; i++) sizes.push(sampleSize);
		}
		return { sizes };
	}

	_parseStco(buf, o) {
		const count = this._readU32(buf, o + 4);
		const out = [];
		let p = o + 8;
		for (let i = 0; i < count; i++) { out.push(this._readU32(buf, p)); p += 4; }
		return out;
	}

	_parseCo64(buf, o) {
		const count = this._readU32(buf, o + 4);
		const out = [];
		let p = o + 8;
		for (let i = 0; i < count; i++) { out.push(this._readU64(buf, p)); p += 8; }
		return out;
	}

	_parseStss(buf, o) {
		const count = this._readU32(buf, o + 4);
		const set = new Set();
		let p = o + 8;
		for (let i = 0; i < count; i++) { set.add(this._readU32(buf, p)); p += 4; }
		return set;
	}

	// Build videoSamples from the video track's stbl tables (non-fragmented MP4),
	// with absolute byteOffsets so _sliceSample reads them directly. Also builds
	// the cts-micros → sample-index map used for per-frame SEI lookup. Returns
	// the sample count, or 0 when there's no usable non-frag table.
	_buildNonFragSamples() {
		const track = this.videoTrack;
		const st = track && track.stbl;
		if (!st || !st.stsz || !st.chunkOffsets || !st.stsc) return 0;

		const sizes = st.stsz.sizes;
		const total = sizes.length;
		if (total === 0) return 0;

		const durations = this._expandStts(st.stts, total);
		const ctsOffsets = st.ctts ? this._expandCtts(st.ctts, total) : null;
		const keySet = st.stss || null; // null → every sample is a keyframe
		const tsc = track.timescale || 1;

		this.videoSamples = [];
		this._sampleIdxByCtsMicros.clear();
		let sampleIdx = 0;
		let dts = 0;
		for (let c = 0; c < st.chunkOffsets.length && sampleIdx < total; c++) {
			const spc = this._samplesPerChunk(st.stsc, c + 1);
			let offset = st.chunkOffsets[c];
			for (let j = 0; j < spc && sampleIdx < total; j++) {
				const size = sizes[sampleIdx];
				const dur = durations[sampleIdx];
				const ctsOff = ctsOffsets ? ctsOffsets[sampleIdx] : 0;
				const cts = dts + ctsOff;
				const isKey = keySet ? keySet.has(sampleIdx + 1) : true;
				this.videoSamples.push({ byteOffset: offset, size, dts, cts, duration: dur, isKey });
				this._sampleIdxByCtsMicros.set(Math.round(cts * 1e6 / tsc), sampleIdx);
				offset += size;
				dts += dur;
				sampleIdx++;
			}
		}
		return this.videoSamples.length;
	}

	_expandStts(entries, total) {
		const out = [];
		if (entries) {
			for (const e of entries) {
				for (let i = 0; i < e.count && out.length < total; i++) out.push(e.delta);
			}
		}
		while (out.length < total) out.push(out.length ? out[out.length - 1] : 0);
		return out;
	}

	_expandCtts(entries, total) {
		const out = [];
		for (const e of entries) {
			for (let i = 0; i < e.count && out.length < total; i++) out.push(e.offset);
		}
		while (out.length < total) out.push(0);
		return out;
	}

	_samplesPerChunk(stsc, chunkNum) {
		let spc = 0;
		for (let i = 0; i < stsc.length; i++) {
			if (chunkNum >= stsc[i].firstChunk) spc = stsc[i].samplesPerChunk;
			else break;
		}
		return spc;
	}

	_onReady() {
		if (!this.videoTrack) throw new Error("No video track found");
		if (!this.videoTrack.codec || !this.videoTrack.description)
			throw new Error("Video codec config missing");

		// Apply the learned hw/sw decoder preference for this codec+resolution (default hardware). If
		// this workload is unknown, _checkForPumping detects + persists during the first ~seconds of play.
		const pref = this._readDecoderPref();
		this.useHardwareAccel = pref.accel;
		this._pumpCheckDone = pref.known;   // skip detection once learned

		this.decoder = this._makeDecoder();
		debugLog(`[${this.canvas.id}] Decoder configured: ${this.videoTrack.codec} (${this.videoTrack.width}x${this.videoTrack.height})`);

		if (this.onDurationChange) this.onDurationChange(this.durationSec);
		if (this.onReady) this.onReady();
		this._updateUI(true);
		if (this.autoplay) this.play();
	}

	_makeDecoder() {
		const d = new VideoDecoder({
			output: frame => {
				if (this._stopped) { frame.close(); return; }
				this.handleFrame(frame);
			},
			error:  err => {
				if (this._stopped) return;
				// Decoder enters terminal 'closed' state on error. Log and let the
				// next user action (play/seek) recreate via _resetAndReconfigure.
				console.warn(`[${this.canvas.id}] Decoder error:`, err);
				if (this.onError) this.onError(err);
			},
		});
		this._configureDecoder(d);
		return d;
	}

	_configureDecoder(d) {
		// Use the browser's normal decoder unless the learned per-codec+resolution preference explicitly
		// selected software. HEVC ignores that H.264 preference and stays on the browser-selected path.
		const accel = (this.videoTrack.codecKind === 'hevc' || this.useHardwareAccel !== false)
			? 'no-preference'
			: 'prefer-software';
		d.configure({
			codec: this.videoTrack.codec,
			description: this.videoTrack.description,
			codedWidth: this.videoTrack.width,
			codedHeight: this.videoTrack.height,
			hardwareAcceleration: accel,
			// Emit each decoded frame ASAP instead of buffering a pipeline of them (helps low-fps clips).
			optimizeForLatency: true,
		});
	}

	// localStorage key for the learned decoder preference — keyed by the DECODE WORKLOAD (codec +
	// resolution), NOT host/camera, so it stays correct across camera swaps + eView members: a stream
	// with a new codec/resolution auto-re-detects; the same workload reuses. Per-browser via localStorage.
	_decoderPrefKey() {
		const t = this.videoTrack || {};
		return decoderPrefKey(t.codecKind, t.width, t.height);
	}

	// Read the stored preference → {accel, known}. known=true → we've learned this workload (skip
	// detection, use it); known=false → default hardware + run pump detection during first playback.
	_readDecoderPref() {
		const t = this.videoTrack || {};
		return readDecoderPref(t.codecKind, t.width, t.height);
	}

	_writeDecoderPref(useSoftware) {
		const t = this.videoTrack || {};
		writeDecoderPref(t.codecKind, t.width, t.height, useSoftware);
	}

	// Detect the hardware-decode "pumping" pattern from the presented-frame cadence and fall back to
	// software once, persisting the choice (ported from live PlayerWS.checkForPumping). A healthy decoder
	// paints at a steady interval; a pumping one BURSTS — several near-0 gaps then long stalls → high
	// variance. Runs only while playing, on hardware, and undecided; one switch max.
	_checkForPumping() {
		if (this._pumpCheckDone || !this.useHardwareAccel) return;
		if (this.videoTrack && this.videoTrack.codecKind === 'hevc') { this._pumpCheckDone = true; return; }
		// A sibling cam with the SAME codec+resolution may have already learned the answer this session
		// (localStorage is shared) — adopt it immediately so only the first cam pays the detection cost,
		// not every cam in a 14-up grid.
		const learned = this._readDecoderPref();
		if (learned.known) {
			this._pumpCheckDone = true;
			if (!learned.accel && this.useHardwareAccel) {
				console.warn(`[${this.canvas.id}] adopting sibling's software preference`);
				this.useHardwareAccel = false;
				this._resetAndReconfigure();
				this._needsKeyframe = true;
			}
			return;
		}
		const pumping = detectPumping(this._renderDeltas);
		if (pumping === null) return;   // < PUMP_MIN_SAMPLES paints — undecided, sample more
		this._pumpCheckDone = true;
		this._writeDecoderPref(pumping);                  // persist hw or sw for this codec+resolution
		if (pumping) {
			console.warn(`[${this.canvas.id}] hardware decode pumping — switching to software`);
			this.useHardwareAccel = false;
			this._resetAndReconfigure();                  // rebuild the decoder with prefer-software
			this._needsKeyframe = true;                   // next feed rewinds to a keyframe
		}
	}

	// Close and replace the decoder. reset()+configure() on the same VideoDecoder
	// appears broken for 4K H.264 in Chrome (accepts decode() calls, never produces
	// output). _minAcceptTsMicros guards against stale frames from the closed
	// decoder still firing output callbacks during the transition.
	_resetAndReconfigure() {
		try { if (this.decoder && this.decoder.state !== 'closed') this.decoder.close(); }
		catch (_) {}
		this.decoder = this._makeDecoder();
		this._needsKeyframe = true;
	}


	// ---- Body buffer management ----

	_appendBody(bytes, absOffset) {
		const rel = absOffset - this.bodyOffset;
		if (rel < 0) return;
		const need = rel + bytes.byteLength;
		if (need > this.bodyBytes.byteLength) {
			let cap = this.bodyBytes.byteLength * 2 || (1024 * 1024);
			while (cap < need) cap *= 2;
			const grown = new Uint8Array(cap);
			grown.set(this.bodyBytes.subarray(0, this.bodyLen));
			this.bodyBytes = grown;
		}
		this.bodyBytes.set(bytes, rel);
		if (need > this.bodyLen) this.bodyLen = need;
		this.bodyEnd = this.bodyOffset + this.bodyLen;
	}

	_parseBody() {
		// Non-fragmented clips have their full sample table from stbl; there are no
		// moof boxes to walk, and the mdat bytes are already addressable by
		// absolute offset. Just refresh the buffered cursor.
		if (!this.isFragmented) { this._updateBuffered(); return; }
		while (true) {
			const rel = this._parseCursor - this.bodyOffset;
			if (rel + 8 > this.bodyLen) break;
			const size = this._readU32(this.bodyBytes, rel);
			const type = this._readType(this.bodyBytes, rel + 4);
			let boxSize, hdrSize = 8;
			if (size === 1) {
				if (rel + 16 > this.bodyLen) break;
				boxSize = this._readU64(this.bodyBytes, rel + 8);
				hdrSize = 16;
			} else if (size === 0) {
				break;
			} else {
				boxSize = size;
			}
			if (rel + boxSize > this.bodyLen) break;

			if (type === 'moof') {
				this._parseMoof(this.bodyBytes, rel + hdrSize, rel + boxSize, this._parseCursor);
			}
			// mdat (and anything else) is just skipped; bytes already in bodyBytes.
			this._parseCursor += boxSize;
		}
		this._updateBuffered();
	}

	_parseMoof(buf, start, end, moofFileOffset) {
		this._walkBoxes(buf, start, end, (t, s, e, h) => {
			if (t === 'traf') this._parseTraf(buf, s + h, e, moofFileOffset);
		});
	}

	// Box-level read of one traf: tfhd defaults, tfdt and every trun. Shared by _parseTraf (sample
	// accrual) and _probeFragmentedDuration (the last fragment's end time).
	_readTraf(buf, start, end) {
		let tfhd = null, tfdt = 0;
		const truns = [];
		this._walkBoxes(buf, start, end, (t, s, e, h) => {
			if (t === 'tfhd') {
				const flags = ((buf[s+h+1] << 16) | (buf[s+h+2] << 8) | buf[s+h+3]) >>> 0;
				let p = s + h + 4;
				const trackID = this._readU32(buf, p); p += 4;
				tfhd = {
					trackID, flags,
					baseDataOffset: null, sampleDescIndex: null,
					defaultSampleDuration: 0, defaultSampleSize: 0, defaultSampleFlags: 0,
					defaultBaseIsMoof: !!(flags & 0x20000),
				};
				if (flags & 0x1)  { tfhd.baseDataOffset      = this._readU64(buf, p); p += 8; }
				if (flags & 0x2)  { tfhd.sampleDescIndex     = this._readU32(buf, p); p += 4; }
				if (flags & 0x8)  { tfhd.defaultSampleDuration = this._readU32(buf, p); p += 4; }
				if (flags & 0x10) { tfhd.defaultSampleSize   = this._readU32(buf, p); p += 4; }
				if (flags & 0x20) { tfhd.defaultSampleFlags  = this._readU32(buf, p); p += 4; }
			} else if (t === 'tfdt') {
				const version = buf[s + h];
				tfdt = (version === 1)
					? this._readU64(buf, s + h + 4)
					: this._readU32(buf, s + h + 4);
			} else if (t === 'trun') {
				const version = buf[s + h];
				const flags = ((buf[s+h+1] << 16) | (buf[s+h+2] << 8) | buf[s+h+3]) >>> 0;
				let p = s + h + 4;
				const count = this._readU32(buf, p); p += 4;
				const trun = { count, dataOffset: 0, firstSampleFlags: null, samples: [] };
				if (flags & 0x1) { trun.dataOffset = (this._readU32(buf, p) | 0); p += 4; }
				if (flags & 0x4) { trun.firstSampleFlags = this._readU32(buf, p); p += 4; }
				for (let i = 0; i < count; i++) {
					const sample = {};
					if (flags & 0x100) { sample.duration = this._readU32(buf, p); p += 4; }
					if (flags & 0x200) { sample.size     = this._readU32(buf, p); p += 4; }
					if (flags & 0x400) { sample.flags    = this._readU32(buf, p); p += 4; }
					if (flags & 0x800) {
						sample.ctsOffset = (version === 0)
							? this._readU32(buf, p)
							: (this._readU32(buf, p) | 0);
						p += 4;
					}
					trun.samples.push(sample);
				}
				truns.push(trun);
			}
		});
		return { tfhd, tfdt, truns };
	}

	_parseTraf(buf, start, end, moofFileOffset) {
		const { tfhd, tfdt, truns } = this._readTraf(buf, start, end);
		if (!tfhd) return;

		// Determine base offset
		let baseDataOffset;
		if (tfhd.baseDataOffset !== null) baseDataOffset = tfhd.baseDataOffset;
		else if (tfhd.defaultBaseIsMoof)  baseDataOffset = moofFileOffset;
		else                               baseDataOffset = moofFileOffset; // first trun fallback

		const isVideo = this.videoTrack && tfhd.trackID === this.videoTrack.id;
		const isMeta  = this.metaTrack  && tfhd.trackID === this.metaTrack.id;
		if (!isVideo && !isMeta) return;
		const dest = isVideo ? this.videoSamples : this.metaSamples;

		let dts = tfdt;
		for (const trun of truns) {
			let cursor = baseDataOffset + trun.dataOffset;
			for (let i = 0; i < trun.samples.length; i++) {
				const s = trun.samples[i];
				const dur  = (s.duration !== undefined) ? s.duration : tfhd.defaultSampleDuration;
				const size = (s.size !== undefined)     ? s.size     : tfhd.defaultSampleSize;
				let sflags;
				if (i === 0 && trun.firstSampleFlags !== null) sflags = trun.firstSampleFlags;
				else if (s.flags !== undefined)               sflags = s.flags;
				else                                          sflags = tfhd.defaultSampleFlags;
				const isKey = !(sflags & 0x00010000);
				const ctsOff = s.ctsOffset || 0;
				dest.push({
					byteOffset: cursor,
					size,
					dts,
					cts: dts + ctsOff,
					duration: dur,
					isKey,
				});
				cursor += size;
				dts += dur;
			}
		}
	}


	// ---- Playback ----

	play() {
		if (this.playing) return;
		if (!this.videoTrack) return;
		this.playing = true;
		this._endedFired = false;
		this.playheadAtStart = this.playheadSec;
		this.startWallTime = performance.now();
		if (this.onStateChange) this.onStateChange('playing');

		// If decoder isn't ready, re-ready it and re-position to a keyframe.
		if (!this.decoder || this.decoder.state !== 'configured') {
			this._resetAndReconfigure();
			const ts = this.playheadSec * this.videoTrack.timescale;
			this.nextVideoIdx = this._findKeyframeIdx(ts);
			this.nextMetaIdx  = this._findMetaIdx(this.videoSamples.length ? this.videoSamples[this.nextVideoIdx].cts : 0);
			this._feedVideo(this.playheadSec);
		}

		if (!this.rafHandle) this._tick();
	}

	pause() {
		if (!this.playing) return;
		this.playheadSec = this.playheadAtStart + (performance.now() - this.startWallTime) / 1000 * this.speed;
		this.playing = false;
		if (this.onStateChange) this.onStateChange('paused');
		// Decoder stays configured — pending samples finish decoding and queue,
		// but _renderQueued (paused + !seekPending) doesn't render them. They'll
		// pop naturally as playhead advances after resume.
	}

	toggle() { if (this.playing) this.pause(); else this.play(); }

	// Show a STILL frame at `sec` while PAUSED (rehydrate after a refresh, or a paused entry) — the
	// analog of a <video> painting the frame at currentTime without playing. seek() renders exactly one
	// frame, but only once the target keyframe's bytes are buffered; if they aren't yet, we remember the
	// request and the load loop retries via _tryFirstPaint as bytes stream in. Without this a paused cell
	// sits on the buffering spinner until the user presses Play (the bug David hit on refresh).
	requestFirstPaint(sec) {
		this._pendingFirstPaintSec = sec;
		this._tryFirstPaint();
	}

	_tryFirstPaint() {
		if (this._pendingFirstPaintSec == null) return;
		// Nothing to do once playing (play() drives its own render) or a frame is already up.
		if (this.playing || this._firstFrameRendered) { this._pendingFirstPaintSec = null; return; }
		if (!this.videoTrack || this.videoSamples.length === 0) return; // no samples yet — retried by load loop
		const sec = this._pendingFirstPaintSec;
		this._pendingFirstPaintSec = null;
		this.seek(sec);   // paused → sets _seekPending; the rAF renders one frame once its bytes decode
	}

	// Change the playback rate. Re-baselines the wall-clock origin so the new rate applies from NOW
	// (not retroactively over already-elapsed time), matching how the shared clock re-scales its tick
	// interval on a speed change. Faithful to Java setSpeed (rate applied forward, playhead preserved).
	setSpeed(s) {
		const next = Number(s) > 0 ? Number(s) : 1;
		if (next === this.speed) return;
		if (this.playing) {
			const now = performance.now();
			this.playheadSec = this.playheadAtStart + (now - this.startWallTime) / 1000 * this.speed;
			this.playheadAtStart = this.playheadSec;
			this.startWallTime = now;
		}
		this.speed = next;
	}

	seek(sec) {
		if (!this.videoTrack || this.videoSamples.length === 0) return;
		this._endedFired = false;
		// An unsettled fragmented length is 0; the buffered clamp below still bounds the seek.
		sec = Math.max(0, this.durationSec > 0 ? Math.min(sec, this.durationSec) : sec);
		if (this.bufferedMaxSec > 0) sec = Math.min(sec, this.bufferedMaxSec);
		const targetTs = sec * this.videoTrack.timescale;
		const kfIdx = this._findKeyframeIdx(targetTs);
		// Snap playhead to the keyframe's actual CTS — matches VLC/<video> semantics
		// and avoids multi-second catch-up delay for high-fps content.
		const kfSec = this.videoSamples[kfIdx].cts / this.videoTrack.timescale;

		// Byte-range seek (non-fragmented / local mpengine only): if the target keyframe's bytes are
		// not in the body buffer, re-anchor the buffer at the keyframe's file offset and refetch from
		// there. First paint then needs only the bytes at/after the target keyframe — not the whole
		// mdat streamed sequentially from the front (the WAN-latency fix; mirrors ffmpeg's mov demuxer
		// byte-range seek). Fragmented (cloud dragon) clips keep sequential fragment loading (their
		// body is moof/mdat streamed from moovEnd, not addressable by a single keyframe offset).
		if (!this.isFragmented) {
			const kf = this.videoSamples[kfIdx];
			const rel = kf.byteOffset - this.bodyOffset;
			const buffered = rel >= 0 && rel + kf.size <= this.bodyLen;
			if (!buffered) {
				this.bodyOffset = kf.byteOffset;
				this.bodyLen = 0;
				this.bodyEnd = kf.byteOffset;
				this.bodyBytes = new Uint8Array(1 << 20);
				this._parseCursor = kf.byteOffset;
				// A new anchor is a new download: earlier failures (or a give-up) do not carry over to it.
				this._refillFailures = 0;
				this._refillRetryAt = 0;
				this._bodyGaveUp = false;
				this._bodyGaveUpReported = false;
				this.loadComplete = false;
				this._streamBodyFrom(kf.byteOffset);
			}
		}

		this._resetAndReconfigure();
		while (this.frameQueue.length > 0) this.frameQueue.shift().close();

		this.nextVideoIdx = kfIdx;
		this.nextMetaIdx  = this._findMetaIdx(this.videoSamples[kfIdx].cts);
		this.playheadSec = kfSec;
		this.playheadAtStart = kfSec;
		this.startWallTime = performance.now();
		this._minAcceptTsMicros = Math.round(kfSec * 1e6);

		if (!this.playing) this._seekPending = true;

		// Paused seek needs a flush to emit the still frame — but deferred until the keyframe is
		// actually fed (which may be after the re-anchored bytes stream in), handled in _feedVideo.
		this._seekFlushPending = !this.playing;
		this._feedVideo(kfSec);

		if (!this.rafHandle) this.rafHandle = requestAnimationFrame(() => this._tick());
		this._updateUI(true);
	}

	_findKeyframeIdx(targetTs) {
		// Sample times are whole ticks. A target rebuilt from seconds (cts / timescale * timescale) can land a
		// hair under the tick it came from, and the raw comparison then picked the PREVIOUS keyframe, whose
		// bytes a byte-range seek never fetched: the paused tile stayed blank until playback started (86bc8571y).
		const t = Math.round(targetTs);
		let idx = -1;
		for (let i = 0; i < this.videoSamples.length; i++) {
			if (this.videoSamples[i].cts > t) break;
			if (this.videoSamples[i].isKey) idx = i;
		}
		if (idx >= 0) return idx;
		// No keyframe at/before the target (target precedes the first IDR, or a fragmented dragon clip
		// whose first buffered sample is a delta). Returning 0 would feed a DELTA after configure() and the
		// decoder throws "a key frame is required after configure()" → the cell blanks. Snap FORWARD to the
		// first real keyframe instead (nothing before the first IDR is decodable anyway).
		for (let i = 0; i < this.videoSamples.length; i++) {
			if (this.videoSamples[i].isKey) return i;
		}
		return 0; // no keyframe anywhere (degenerate; shouldn't happen for a decodable clip)
	}

	_findMetaIdx(videoTs) {
		if (!this.metaTrack || this.metaSamples.length === 0) return 0;
		const metaTs = videoTs * this.metaTrack.timescale / this.videoTrack.timescale;
		let idx = 0;
		for (let i = 0; i < this.metaSamples.length; i++) {
			if (this.metaSamples[i].cts > metaTs) break;
			idx = i;
		}
		return idx;
	}

	_tick() {
		this.rafHandle = null;

		if (this.playing) {
			const now = performance.now();
			this.playheadSec = this.playheadAtStart + (now - this.startWallTime) / 1000 * this.speed;

			if (this.durationSec > 0 && this.playheadSec >= this.durationSec) {
				this.playheadSec = this.durationSec;
				this.pause();
				if (!this._endedFired) {
					this._endedFired = true;
					if (this.onEnded) this.onEnded();
				}
			}

			this._feedVideo(this.playheadSec);
			this._maybeRefillBody();
			this._reportBodyGaveUp();
		} else if (this._seekPending) {
			// Paused seek (first-paint after a refresh, or a paused scrub): keep feeding toward the target
			// so the still frame decodes EVEN IF the keyframe's bytes weren't buffered when seek() first
			// ran (the clip is still streaming). Without this a paused cell sits on a spinner forever —
			// _tick fed only while playing, so a paused seek never re-fed once bytes arrived and the only
			// escape was pressing Play (which flips `playing` true). This was David's refresh spinner bug.
			this._feedVideo(this.playheadSec);
			// A paused window that failed retries here too: _maybeRefillBody otherwise runs only while
			// playing, and a paused tile would stay blank until Play after one transient error.
			if (this._refillFailures > 0) this._maybeRefillBody();
		}

		this._renderQueued();
		this._updateUI();

		// Keep ticking while playing, or while a paused-seek still needs to render.
		if (this.playing || this._seekPending) {
			this.rafHandle = requestAnimationFrame(() => this._tick());
		}
	}

	// Strip SEI NALs from a length-prefixed sample before it goes to the decoder.
	// mpengine/iSite embed a b2 "user_data_unregistered" SEI (H.264 NAL type 6,
	// HEVC types 39/40) whose payload uses a non-standard escape (AA AA AB) and
	// carries long 00 00 00 runs (the null-padded camera name). Chrome's H.264
	// decoder chokes on it ("Unable to determine size of bitstream buffer") and
	// enters the terminal closed state, so nothing paints. The decoder never needs
	// the SEI — b2 metadata is read straight off the raw sample (parseB2FromSample)
	// for the HUD/overlays — so we hand the decoder a SEI-free copy. Zero-copy when
	// there is no SEI, or when the SEI NALs form a contiguous prefix (the common
	// mpengine layout: SEI first, then SPS/PPS/VCL).
	_stripSeiForDecode(bytes) {
		const nls = (this.videoTrack && this.videoTrack.nalLengthSize) || 4;
		const isHevc = this.videoTrack && this.videoTrack.codecKind === 'hevc';
		const isSei = (hdr) => isHevc ? (((hdr >> 1) & 0x3f) === 39 || ((hdr >> 1) & 0x3f) === 40)
		                              : ((hdr & 0x1f) === 6);
		const codec = isHevc ? 'hevc' : 'h264';
		const keep = []; // contiguous [start,end) segments to feed the decoder
		let sawSei = false;
		let p = 0;
		while (p + nls <= bytes.length) {
			let L = 0;
			for (let i = 0; i < nls; i++) L = (L << 8) | bytes[p + i];
			L = L >>> 0;
			const naluStart = p;
			const naluEnd = p + nls + L;
			// Bail on a malformed length (0 or past the buffer) — feed the sample
			// untouched rather than risk corrupting a valid bitstream.
			if (L === 0 || naluEnd > bytes.length) return bytes;
			if (isSei(bytes[p + nls]) && findSeiInSample(bytes.subarray(naluStart, naluEnd), codec, { nalLengthSize: nls })) {
				sawSei = true;
			} else {
				const last = keep[keep.length - 1];
				if (last && last[1] === naluStart) last[1] = naluEnd; // extend contiguous run
				else keep.push([naluStart, naluEnd]);
			}
			p = naluEnd;
		}
		if (!sawSei) return bytes;                 // nothing stripped — original buffer
		if (keep.length === 0) return new Uint8Array(0);
		if (keep.length === 1) return bytes.subarray(keep[0][0], keep[0][1]); // zero-copy
		let total = 0; for (const [a, b] of keep) total += b - a;
		const out = new Uint8Array(total);
		let q = 0; for (const [a, b] of keep) { out.set(bytes.subarray(a, b), q); q += b - a; }
		return out;
	}

	_feedVideo(uptoSec) {
		if (!this.videoTrack || !this.decoder) return;
		// Never call decode() on a closed/unconfigured decoder — it throws every
		// tick and blanks the canvas. play()/seek() recreate it via _resetAndReconfigure.
		if (this.decoder.state !== 'configured') return;

		// If the decoder was just (re)configured or flushed, its first decode
		// MUST be a key chunk. Rewind nextVideoIdx to the nearest keyframe so
		// our next feed starts with one.
		if (this._needsKeyframe) {
			const ts = uptoSec * this.videoTrack.timescale;
			this.nextVideoIdx = this._findKeyframeIdx(ts);
		}

		const lookaheadTs = (uptoSec + 0.5) * this.videoTrack.timescale;
		const QUEUE_HIGH = 12;
		let fed = 0;
		while (this.nextVideoIdx < this.videoSamples.length) {
			const s = this.videoSamples[this.nextVideoIdx];
			if (s.dts > lookaheadTs) break;
			if ((this.decoder.decodeQueueSize || 0) >= QUEUE_HIGH) break;
			const bytes = this._sliceSample(s);
			if (!bytes) break; // not yet buffered
			try {
				this.decoder.decode(new EncodedVideoChunk({
					type: s.isKey ? 'key' : 'delta',
					timestamp: Math.round(s.cts * 1e6 / this.videoTrack.timescale),
					duration: Math.round(s.duration * 1e6 / this.videoTrack.timescale),
					data: this._stripSeiForDecode(bytes),
				}));
				if (s.isKey) this._needsKeyframe = false;
			} catch (e) {
				console.warn('decode error:', e);
				break;
			}
			this.nextVideoIdx++;
			fed++;
		}

		// Paused seek: the decoder buffers input and won't emit the still frame until flushed. seek()
		// can't flush usefully when the target bytes arrive AFTER it runs (the byte-range re-anchor),
		// so flush HERE, once the keyframe has actually been fed — that forces the seek frame out to
		// paint. Guarded to fire once per seek (_seekFlushPending), only while paused.
		if (fed > 0 && this._seekFlushPending && !this.playing && this.decoder && this.decoder.state === 'configured') {
			this._seekFlushPending = false;
			// The decoder needs a key chunk from the moment flush() is CALLED, not when it resolves: a
			// delta streamed in on the next tick, while the flush is still pending, throws "Key frame is
			// required". Rewinding to the keyframe now keeps the next feed valid.
			this._needsKeyframe = true;
			this.decoder.flush().then(() => { this._needsKeyframe = true; }).catch(() => {});
		}
	}

	_sliceSample(s) {
		const rel = s.byteOffset - this.bodyOffset;
		if (rel < 0 || rel + s.size > this.bodyLen) return null;
		return this.bodyBytes.subarray(rel, rel + s.size);
	}

	_pickMetadataAt(videoTs) {
		if (!this.metaTrack || this.metaSamples.length === 0) return null;
		const metaTs = videoTs * this.metaTrack.timescale / this.videoTrack.timescale;
		while (this.nextMetaIdx + 1 < this.metaSamples.length &&
		       this.metaSamples[this.nextMetaIdx + 1].cts <= metaTs) {
			this.nextMetaIdx++;
		}
		while (this.nextMetaIdx > 0 && this.metaSamples[this.nextMetaIdx].cts > metaTs) {
			this.nextMetaIdx--;
		}
		const cand = this.metaSamples[this.nextMetaIdx];
		return (cand && cand.cts <= metaTs) ? cand : null;
	}

	_updateBuffered() {
		if (this.videoSamples.length === 0 || !this.videoTrack) return;
		const last = this.videoSamples[this.videoSamples.length - 1];
		this.bufferedMaxSec = (last.cts + last.duration) / this.videoTrack.timescale;
		const before = this.durationSec;
		if (this.isFragmented && this.loadComplete && (this.totalSize ? this.bodyEnd >= this.totalSize : this._bodyEOF)) {
			// Every byte is in: the last sample's end is authoritative. A stream that FAILED also sets
			// loadComplete; its partial end must not become the length, or _tick ends the cell there.
			this._durationSettled = true;
			this.durationSec = this.bufferedMaxSec;
		} else if (this.isFragmented && !this._durationSettled) {
			return; // real length not read yet; keep 0 rather than publish the downloaded length
		} else if (this.isFragmented && this._durationEstimated && this.totalSize > 0) {
			// Parsed bytes, not received bytes: bufferedMaxSec only covers parsed fragments, and counting
			// a half-received one would dip the estimate below the playhead and end the clip early.
			const have = this._parseCursor;
			if (have > this.moovEnd) this.durationSec = this.bufferedMaxSec * (this.totalSize - this.moovEnd) / (have - this.moovEnd);
		}
		if (this.durationSec < this.bufferedMaxSec) this.durationSec = this.bufferedMaxSec;
		if (this.durationSec !== before && this.onDurationChange) this.onDurationChange(this.durationSec);
	}

	// Read a fragmented clip's real length off its tail index, then settle durationSec. Failure or a
	// 5 s timeout falls back to the byte-share estimate; load completion supersedes either.
	_probeDurationAsync() {
		// Own controller: a timed-out probe stops fetching instead of competing with the body stream.
		const ac = new AbortController();
		const onStop = () => ac.abort();
		this.abortController.signal.addEventListener('abort', onStop);
		let timer;
		const timeout = new Promise(res => { timer = setTimeout(() => { ac.abort(); res(0); }, PlaybackPlayer.DURATION_PROBE_TIMEOUT_MS); });
		Promise.race([this._probeFragmentedDuration(ac.signal), timeout]).catch(() => 0).then((sec) => {
			clearTimeout(timer);
			this.abortController.signal.removeEventListener('abort', onStop);
			if (this._stopped || this._durationSettled) return;
			this._durationSettled = true;
			if (sec > 0) this.durationSec = Math.max(sec, this.bufferedMaxSec);
			else this._durationEstimated = true;
			if (this.durationSec > 0 && this.onDurationChange) this.onDurationChange(this.durationSec);
			this._updateBuffered();
		});
	}

	// mpengine frag clips end with an mfra index (ffmpeg writes it on a clean close) whose mfro
	// trailer gives its size. The last tfra entry points at the last moof; its tfdt plus the trun
	// durations and CTS offsets is the presentation end. One suffix read covers mfro + mfra, then one
	// read for the last moof. Returns 0 when there is no usable index (crash-truncated recording).
	async _probeFragmentedDuration(signal) {
		const track = this.videoTrack;
		if (!track || !track.timescale) return 0;
		const TAIL = 65536;
		const tr = await fetch(this.fileUrl, { headers: { Range: `bytes=-${TAIL}` }, signal });
		const m = ((tr.headers && tr.headers.get('Content-Range')) || '').match(/bytes (\d+)-\d+\/(\d+)$/);
		if (tr.status !== 206 || !m) { try { if (tr.body) tr.body.cancel(); } catch (_) {} return 0; }
		const tail = new Uint8Array(await tr.arrayBuffer());
		const tailStart = parseInt(m[1], 10), total = parseInt(m[2], 10);
		if (!this.totalSize) this.totalSize = total;
		if (total !== this.totalSize) return 0;
		const range = async (from, to) => {
			if (from >= tailStart) return tail.subarray(from - tailStart, to - tailStart);
			const r = await fetch(this.fileUrl, { headers: { Range: `bytes=${from}-${to - 1}` }, signal });
			if (r.status !== 206) throw new Error(`HTTP ${r.status}`);
			return new Uint8Array(await r.arrayBuffer());
		};

		if (tail.byteLength < 16 || this._readType(tail, tail.byteLength - 12) !== 'mfro') return 0;
		const mfraSize = this._readU32(tail, tail.byteLength - 4);
		if (mfraSize < 16 || mfraSize > total) return 0;
		const mfra = await range(total - mfraSize, total);
		if (this._readType(mfra, 4) !== 'mfra') return 0;

		let lastMoof = -1;
		this._walkBoxes(mfra, 8, mfra.byteLength, (t, s, e, h) => {
			if (t !== 'tfra') return;
			const version = mfra[s + h];
			let p = s + h + 4;
			if (this._readU32(mfra, p) !== track.id) return;
			p += 4;
			const lengths = this._readU32(mfra, p); p += 4;
			const count = this._readU32(mfra, p); p += 4;
			const entryLen = (version === 1 ? 16 : 8)
				+ ((lengths >> 4) & 3) + 1 + ((lengths >> 2) & 3) + 1 + (lengths & 3) + 1;
			if (count === 0) return;
			const last = p + (count - 1) * entryLen;
			if (last + entryLen > e) return; // truncated index: keep the fallback
			lastMoof = version === 1 ? this._readU64(mfra, last + 8) : this._readU32(mfra, last + 4);
		});
		if (lastMoof < 0 || lastMoof >= total) return 0;

		let moof = await range(lastMoof, Math.min(total, lastMoof + TAIL));
		if (moof.byteLength < 8 || this._readType(moof, 4) !== 'moof') return 0;
		let moofSize = this._readU32(moof, 0), hdr = 8;
		if (moofSize === 1 && moof.byteLength >= 16) { moofSize = this._readU64(moof, 8); hdr = 16; }
		if (moofSize < hdr || lastMoof + moofSize > total) return 0;
		if (moofSize > moof.byteLength) moof = await range(lastMoof, lastMoof + moofSize);
		let endTicks = 0;
		this._walkBoxes(moof, hdr, moofSize, (t, s, e, h) => {
			if (t !== 'traf') return;
			const { tfhd, tfdt, truns } = this._readTraf(moof, s + h, e);
			if (!tfhd || tfhd.trackID !== track.id) return;
			let dts = tfdt;
			for (const trun of truns) {
				for (const smp of trun.samples) {
					const dur = smp.duration !== undefined ? smp.duration : tfhd.defaultSampleDuration;
					endTicks = Math.max(endTicks, dts + (smp.ctsOffset || 0) + dur);
					dts += dur;
				}
			}
		});
		return endTicks / track.timescale;
	}

	// Emit the current playhead/duration to the component. Throttled: the rAF loop
	// runs ~60fps, but React should re-render far less; force=true bypasses it for
	// discrete events (ready, seek).
	_updateUI(force = false) {
		if (!this.onTimeUpdate || !(this.durationSec > 0)) return;
		if (!force && this._lastEmitSec != null &&
		    Math.abs(this.playheadSec - this._lastEmitSec) < 0.2) return;
		this._lastEmitSec = this.playheadSec;
		this.onTimeUpdate(this.playheadSec, this.durationSec);
	}


	// ---- Frame render ----

	handleFrame(frame) {
		// Drop stale frames from a previous decoder state (defensive — decoder.reset()
		// should discard pending outputs per spec, but in practice callbacks can slip
		// through during the reset/configure transition).
		if (frame.timestamp < this._minAcceptTsMicros) {
			frame.close();
			return;
		}
		this.frameQueue.push(frame);
		while (this.frameQueue.length > 30) this.frameQueue.shift().close();

		// If paused but the rAF loop isn't running (seek while paused), kick it once.
		if (!this.rafHandle) this.rafHandle = requestAnimationFrame(() => this._tick());
	}

	_renderQueued() {
		if (this.frameQueue.length === 0) return;
		const playheadMicros = this.playheadSec * 1e6;
		let toRender = null;

		if (this.playing) {
			// Playing: render the latest frame with ts ≤ playhead, drop older ones.
			while (this.frameQueue.length > 0 &&
			       this.frameQueue[0].timestamp <= playheadMicros) {
				if (toRender) toRender.close();
				toRender = this.frameQueue.shift();
			}
		} else if (this._seekPending) {
			// Paused + seek: render exactly one frame at (or closest to) the new position,
			// then stop until the user resumes or seeks again.
			while (this.frameQueue.length > 0 &&
			       this.frameQueue[0].timestamp <= playheadMicros) {
				if (toRender) toRender.close();
				toRender = this.frameQueue.shift();
			}
			if (!toRender && this.frameQueue.length > 0) {
				toRender = this.frameQueue.shift();
			}
			if (toRender) this._seekPending = false;
		}
		// else: paused with no pending seek — leave frames in queue; decoder is closed so it's fine.

		if (toRender) {
			this._renderFrame(toRender);
			toRender.close();
		}
	}

	// Draws one frame + metadata overlays through the shared overlayRenderer.
	_renderFrame(frame) {
		// Sample the presented-frame cadence for the info overlay (rolling window, newest last).
		const nowWall = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
		if (this._lastRenderWall) {
			const dt = nowWall - this._lastRenderWall;
			if (dt > 0) {
				this._fpsSamples.push(1000 / dt);
				if (this._fpsSamples.length > 12) this._fpsSamples.shift();
				// Sample the paint cadence for hw→sw pump detection (only while playing, still on
				// hardware, and undecided). A pumping hardware decoder bursts; _checkForPumping reads it.
				if (this.playing && this.useHardwareAccel && !this._pumpCheckDone) {
					this._renderDeltas.push(dt);
					if (this._renderDeltas.length > 40) this._renderDeltas.shift();
					this._checkForPumping();
				}
			}
		}
		this._lastRenderWall = nowWall;

		this.canvas.width  = frame.displayWidth  || frame.codedWidth;
		this.canvas.height = frame.displayHeight || frame.codedHeight;
		this.ctx.drawImage(frame, 0, 0, this.canvas.width, this.canvas.height);

		// Metadata source: local (non-fragmented) mpengine clips carry b2 SEI in
		// the video NALs; cloud (fragmented) clips carry a JSON metadata track.
		// Prefer SEI only for a confirmed non-frag clip (load() sets isFragmented
		// = false and builds the cts→sample map); everything else uses the track.
		const sei = this.isFragmented === false ? this._seiForFrame(frame.timestamp) : null;
		if (sei) {
			this.metadata = b2ToOverlayMetadata(sei);
			if (this.onMetadataUpdate) this.onMetadataUpdate(this.metadata);
		} else {
			// Pick + parse the metadata sample aligned to this frame's timestamp.
			const videoTs = frame.timestamp * this.videoTrack.timescale / 1e6;
			const metaSample = this._pickMetadataAt(videoTs);
			if (metaSample) {
				const bytes = this._sliceSample(metaSample);
				if (bytes) {
					try {
						this.metadata = JSON.parse(new TextDecoder().decode(bytes));
						if (this.onMetadataUpdate) this.onMetadataUpdate(this.metadata);
					} catch (_) { /* leave prior metadata */ }
				}
			}
		}

		// Object-event publish on the CLEAN frame, before overlays (same as live).
		this.publishObjectEvents();

		// Shared overlay renderer — objects + LPR ROI/plates, one source of truth.
		drawOverlays(this.ctx, this.canvas.width, this.canvas.height, this.metadata, {
			showObjects: this.showObjects,
			showLpr: this.showLpr,
		});

		// SEI-sourced camera-name + timestamp HUD, burned onto the canvas (so it
		// survives fullscreen + screenshot). Local clips only; cloud clips show
		// their name via the component's DOM chrome.
		if (sei && this.showHud) this._drawSeiHud(sei);

		// First painted frame → tell the buffer-gate the picture is genuinely up (loadeddata analog).
		if (!this._firstFrameRendered) {
			this._firstFrameRendered = true;
			if (this.onFirstFrame) this.onFirstFrame();
		} else if (this.onProgress) {
			// Self-heal: ANY painted frame (playing OR a paused-seek repaint) means the picture is up →
			// NOT buffering. Clears a spinner that latched report(true) with no reconcile — including the
			// paused-repaint case (rapid scrub / clip re-seek) where onFirstFrame won't re-fire and, before
			// this dropped the `playing` guard, the loader stayed stuck over an already-painted cell.
			this.onProgress();
		}
	}

	// Return the parsed b2 SEI for the frame at `tsMicros` (a sample's composition
	// time in micros — the same value fed as EncodedVideoChunk.timestamp). Parsed
	// lazily and cached; null is cached too so SEI-less samples aren't re-parsed.
	// Falls back to the last-seen SEI for sparse-SEI clips.
	_seiForFrame(tsMicros) {
		if (this.seiByCtsMicros.has(tsMicros)) {
			return this.seiByCtsMicros.get(tsMicros) || this._lastSeiSeen;
		}
		let parsed = null;
		const idx = this._sampleIdxByCtsMicros.get(tsMicros);
		if (idx != null && this.videoSamples[idx] && this.videoTrack && this.videoTrack.codecKind) {
			const bytes = this._sliceSample(this.videoSamples[idx]);
			if (bytes) parsed = parseB2FromSample(bytes, this.videoTrack.codecKind, {
				nalLengthSize: this.videoTrack.nalLengthSize || 4,
			});
		}
		this.seiByCtsMicros.set(tsMicros, parsed);
		if (parsed) this._lastSeiSeen = parsed;
		return parsed || this._lastSeiSeen;
	}

	// Bottom-left CCTV HUD: camera name + local timestamp from the SEI header.
	_drawSeiHud(sei) {
		const name = (sei.header.name || '').trim();
		const ts = sei.header.timestamp;
		const timeStr = ts ? this._formatSeiTime(ts, sei.header.gmtoffset || 0) : '';
		if (!name && !timeStr) return;

		const ctx = this.ctx;
		const W = this.canvas.width;
		const H = this.canvas.height;
		const fontSize = Math.max(14, Math.round(W / 80));
		const pad = Math.max(4, Math.round(fontSize / 3));
		const lineH = fontSize + pad;
		const lines = timeStr ? [name || 'Camera', timeStr] : [name];

		ctx.save();
		ctx.font = `bold ${fontSize}px sans-serif`;
		ctx.textBaseline = 'top';
		let maxW = 0;
		for (const l of lines) maxW = Math.max(maxW, ctx.measureText(l).width);
		const boxW = maxW + pad * 2;
		const boxH = lineH * lines.length + pad;
		const x = pad;
		const y = H - boxH - pad;
		ctx.fillStyle = 'rgba(0,0,0,0.6)';
		ctx.fillRect(x, y, boxW, boxH);
		ctx.fillStyle = '#fff';
		for (let i = 0; i < lines.length; i++) {
			ctx.fillText(lines[i], x + pad, y + pad + i * lineH);
		}
		ctx.restore();
	}

	// Format a Unix-seconds SEI timestamp in the NVR's local time (gmtoffset is
	// the NVR's whole-hour offset from UTC). JS Date is UTC under the hood, so
	// shift by the difference between the viewer's offset and the NVR's.
	_formatSeiTime(unixSec, gmtoffsetHours) {
		const date = new Date(unixSec * 1000);
		const viewerOffsetMin = date.getTimezoneOffset();
		const nvrOffsetMin = -gmtoffsetHours * 60;
		const shifted = new Date(date.getTime() + (viewerOffsetMin - nvrOffsetMin) * 60 * 1000);
		return shifted.toLocaleString();
	}

	stop() {
		this._stopped = true;
		this.playing = false;
		if (this._overlayUnsub) { this._overlayUnsub(); this._overlayUnsub = null; }
		if (this.rafHandle) cancelAnimationFrame(this.rafHandle);
		this.rafHandle = null;
		try { if (this.abortController) this.abortController.abort(); } catch (_) {}
		try { if (this._bodyAbort) this._bodyAbort.abort(); } catch (_) {}
		try { if (this.decoder && this.decoder.state !== 'closed') this.decoder.close(); } catch (_) {}
		while (this.frameQueue.length > 0) this.frameQueue.shift().close();
		this.decoder = null;
	}
}

// Longest a fragmented clip waits for its tail index before using the byte-share estimate.
PlaybackPlayer.DURATION_PROBE_TIMEOUT_MS = 5000;

// Non-fragmented body windows (86bc8571y). Paused covers a first paint or a warm next-clip player;
// playing windows are topped up once the playhead is within the low-water mark of the buffered end.
PlaybackPlayer.BODY_WINDOW_PAUSED_SEC = 4;
PlaybackPlayer.BODY_WINDOW_PLAY_SEC = 20;
PlaybackPlayer.BODY_REFILL_LOW_WATER_SEC = 8;
PlaybackPlayer.BODY_REFILL_MAX_FAILURES = 3;
// Larger than any real index (a 5-minute clip's moov is ~20 KB); past this the tail is not a moov.
PlaybackPlayer.TAIL_MOOV_MAX_BYTES = 16 * 1024 * 1024;
// Suffix read started with the head probe; covers the moov of any clip up to ~15 minutes.
PlaybackPlayer.TAIL_PROBE_BYTES = 65536;
// Longest the load waits on that suffix read once the head shows a tail moov.
PlaybackPlayer.TAIL_PROBE_TIMEOUT_MS = 4000;

export default PlaybackPlayer;
